import { ROOM_CHECK_IDS_MAX, type RoomCheckResult, type StayGroup, formatFlightDesignator } from "@seagames/contracts";
import { Modal, ModalActions, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type DeskFilter, checkAssignments, deskPeopleOptions } from "../../lib/api/accommodation";
import { assignmentState } from "../../lib/accommodation/types";
import { formatClockIn, formatDateTime, formatDayShort, wallTimeIn } from "../../lib/format/datetime";
import { toast } from "../../lib/ui/toast";
import { Bar, FlagTag } from "../accommodation/parts";
import { DoorIcon, ListLinesIcon } from "../helpdesk/icons";
import { FlightStatusChip, ModeIcon } from "../travel-leg-form";
import type { HotelGuest } from "./api";
import { GuestRow, UndoCheck } from "./guest-row";

export type DeskDirection = "arrivals" | "departures";

/** "2:30PM" on the games' clock, the viewer's own time in the tooltip (M9 T6). */
function Clock({ at, timeZone }: { at: string | null; timeZone: string }) {
	if (!at) return <span>—</span>;
	return (
		<time dateTime={at} title={formatDateTime(at)} className="tabular-nums">
			{formatClockIn(at, timeZone)}
		</time>
	);
}

/** "MH 803", a coach / ferry service number, or null (no travel on file). */
export function groupName(g: Pick<StayGroup, "flight" | "service_no">): string | null {
	return g.flight ? formatFlightDesignator(g.flight.carrier_code, g.flight.flight_number) : g.service_no;
}

/**
 * One arrival (or departure) at the hotel — her check-in group card, phone
 * first: what they come on, the window they reach the desk in (computed by
 * the API on every read from the flight's expected time + the entry point's
 * dwell + the drive, so a delay moves it by itself), the contingents (codes
 * only), the progress, and People / Check in N. Departures say "Check out
 * by" (the transfer's pickup, else the flight − lead − drive, else the
 * building's check-out time).
 */
export function DeskGroupCard({
	group,
	direction,
	timeZone,
	date,
	canCheck,
	onOpen,
}: {
	group: StayGroup;
	direction: DeskDirection;
	timeZone: string;
	/** The desk's day (games' zone): a window on another day names its day ("Thu 15 Oct"). */
	date: string;
	canCheck: boolean;
	onOpen: () => void;
}) {
	const { t } = useTranslation("helpdesk");
	const { formatNumber } = useUiStrings();
	const windowDay = wallTimeIn(direction === "arrivals" ? group.window_from : group.check_out_by, timeZone)?.date;
	const left = group.total - group.done;
	const arrivals = direction === "arrivals";
	const name = groupName(group);
	const f = group.flight;
	const moved = f?.scheduled_at && f.expected_at && f.scheduled_at !== f.expected_at;
	const orgs = group.organizations.join(", ");

	return (
		<article className={`grid gap-[0.625rem] rounded-box border bg-base-100 px-[1rem] py-[0.875rem] ${group.late ? "border-gms-warn/40" : "border-gms-line"}`}>
			<header className="flex min-w-0 items-start justify-between gap-[0.5rem]">
				<span className="flex min-w-0 items-center gap-[0.5rem]">
					<span className="grid size-[2rem] shrink-0 place-items-center rounded-[0.5rem] bg-gms-navy-soft text-gms-navy [&>svg]:size-[1rem]">
						{group.kind === "NO_TRAVEL" ? <ListLinesIcon /> : <ModeIcon mode={group.kind === "FLIGHT" ? "AIR" : "LAND"} />}
					</span>
					<span className="grid min-w-0">
						<b className="truncate text-[0.9375rem] leading-tight font-semibold text-gms-navy-ink">{name ?? t("hotel.group.noTravel")}</b>
						<span className="truncate text-[0.75rem] text-gms-ink-3">
							{group.entry_point ? group.entry_point.name : t("hotel.group.ownWay")}
							{f?.expected_at && (
								<>
									<span aria-hidden> · </span>
									{arrivals ? t("hotel.group.lands") : t("hotel.group.departs")} <Clock at={f.expected_at} timeZone={timeZone} />
									{moved && (
										<>
											{" "}
											<s>
												<Clock at={f.scheduled_at} timeZone={timeZone} />
											</s>
										</>
									)}
								</>
							)}
						</span>
					</span>
				</span>
				{f && <FlightStatusChip status={f.status} />}
			</header>

			{/* The window: her big digits. */}
			<div className="flex flex-wrap items-baseline gap-x-[0.625rem] gap-y-[0.25rem]">
				<span className="text-[0.75rem] font-medium text-gms-ink-3">{arrivals ? t("hotel.group.atHotel") : t("hotel.group.checkOutBy")}</span>
				<b className="font-display text-[1.375rem] leading-none font-bold text-gms-navy-ink">
					{arrivals ? (
						group.window_from ? (
							<>
								<Clock at={group.window_from} timeZone={timeZone} />
								<span aria-hidden className="px-[0.25rem] text-gms-ink-3">
									–
								</span>
								<Clock at={group.window_to} timeZone={timeZone} />
							</>
						) : (
							<span className="text-[1rem] text-gms-ink-3">{t("hotel.group.noWindow")}</span>
						)
					) : (
						<Clock at={group.check_out_by} timeZone={timeZone} />
					)}
				</b>
				{windowDay && windowDay !== date && <span className="text-[0.75rem] font-semibold text-gms-ink-2">{formatDayShort(windowDay)}</span>}
				{group.late && <FlagTag flag={arrivals ? "LATE_ARRIVAL" : "LATE_CHECKOUT"} />}
			</div>

			{orgs && (
				<p className="truncate text-[0.78125rem] text-gms-ink-2">
					{group.organizations_more > 0 ? t("hotel.group.orgsMore", { orgs, n: formatNumber(group.organizations_more) }) : orgs}
				</p>
			)}

			<div className="grid gap-[0.3125rem]">
				<Bar value={group.done} max={group.total} tone={left === 0 ? "ok" : undefined} className="w-full" />
				<b className="text-[0.78125rem] font-semibold text-gms-navy-ink tabular-nums">
					{arrivals
						? t("hotel.group.inOf", { done: formatNumber(group.done), total: formatNumber(group.total) })
						: t("hotel.group.outOf", { done: formatNumber(group.done), total: formatNumber(group.total) })}
				</b>
			</div>

			<div className="flex flex-wrap justify-end gap-[0.5rem]">
				<button type="button" className="btn btn-sm btn-line max-[30rem]:flex-1" onClick={onOpen}>
					<ListLinesIcon className="size-[0.9375rem]" />
					{t("hotel.group.people")}
				</button>
				{canCheck &&
					(left > 0 ? (
						<button type="button" className="btn btn-sm btn-primary max-[30rem]:flex-1" onClick={onOpen}>
							<DoorIcon className="size-[0.9375rem]" />
							{arrivals ? t("hotel.group.checkIn", { n: formatNumber(left) }) : t("hotel.group.checkOut", { n: formatNumber(left) })}
						</button>
					) : (
						<span className="inline-flex h-[2rem] items-center rounded-[0.375rem] bg-gms-ok-soft px-[0.625rem] text-[0.75rem] font-semibold text-gms-ok">{t("hotel.group.done")}</span>
					))}
			</div>
		</article>
	);
}

/**
 * A group's people (her `peopleSheet` + `checkSheet` in one): every guest
 * with the fields a hotel may see; with the desk permission, a tick per
 * guest who can be checked in (not in yet) or out (in), all ticked to
 * start, then ONE bulk call (≤ ROOM_CHECK_IDS_MAX). The API names each
 * refusal (card not ready, stay not started…); they stay on the sheet.
 */
export function DeskGroupSheet({
	group,
	direction,
	filter,
	timeZone,
	canCheck,
	onClose,
}: {
	group: StayGroup;
	direction: DeskDirection;
	filter: DeskFilter;
	timeZone: string;
	canCheck: boolean;
	onClose: () => void;
}) {
	const { t } = useTranslation("helpdesk");
	const { formatNumber } = useUiStrings();
	const people = useQuery(deskPeopleOptions(direction, filter, group.group_key));
	const guests: HotelGuest[] = people.data?.items ?? [];
	const arrivals = direction === "arrivals";
	const actionable = useMemo(() => guests.filter((g) => assignmentState(g.assignment) === (arrivals ? "ASSIGNED" : "IN")), [guests, arrivals]);
	const [picked, setPicked] = useState<Set<string> | null>(null);
	const selected = picked ?? new Set(actionable.map((g) => g.room_assignment_id));
	const [busy, setBusy] = useState(false);
	const [result, setResult] = useState<RoomCheckResult | null>(null);
	// Names of the rows a call was sent for, kept: a guest who just left the group (refetch) still reads by name (E2E B6).
	const [sentNames, setSentNames] = useState<Map<string, string>>(new Map());
	const nameOf = (id: string) => sentNames.get(id) ?? guests.find((g) => g.room_assignment_id === id)?.name ?? "—";

	const toggle = (id: string) => {
		const next = new Set(selected);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		setPicked(next);
	};

	const run = async () => {
		const ids = [...selected].slice(0, ROOM_CHECK_IDS_MAX);
		if (ids.length === 0) return;
		setSentNames(new Map(guests.filter((g) => ids.includes(g.room_assignment_id)).map((g) => [g.room_assignment_id, g.name])));
		setBusy(true);
		try {
			const res = await checkAssignments(arrivals ? "IN" : "OUT", ids);
			setResult(res);
			setPicked(new Set());
			const n = res.done.length;
			if (n > 0) toast.success(arrivals ? t("hotel.checkedIn", { count: n, n: formatNumber(n) }) : t("hotel.checkedOut", { count: n, n: formatNumber(n) }));
			if (res.skipped.length === 0) onClose();
		} catch (err) {
			toast.error(err, arrivals ? t("hotel.checkInFailed") : t("hotel.checkOutFailed"));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open
			onClose={onClose}
			size="lg"
			title={groupName(group) ?? t("hotel.group.noTravel")}
			description={t("hotel.sheet.description", { count: group.total, n: formatNumber(group.total) })}
			dismissible={!busy}
			footer={
				<ModalActions start={canCheck && actionable.length > 0 ? <span className="text-[0.78125rem] text-gms-ink-3">{arrivals ? t("hotel.sheet.keysIn") : t("hotel.sheet.keysOut")}</span> : undefined}>
					<button type="button" className="btn btn-sm btn-line" onClick={onClose} disabled={busy}>
						{t("common.close")}
					</button>
					{canCheck && actionable.length > 0 && (
						<button type="button" className="btn btn-sm btn-primary" disabled={busy || selected.size === 0} onClick={() => void run()}>
							{busy ? <span className="loading loading-spinner loading-xs" /> : <DoorIcon className="size-[0.9375rem]" />}
							{arrivals ? t("hotel.group.checkIn", { n: formatNumber(selected.size) }) : t("hotel.group.checkOut", { n: formatNumber(selected.size) })}
						</button>
					)}
				</ModalActions>
			}
		>
			{result && result.skipped.length > 0 && (
				<div role="status" className="mb-[0.75rem] grid gap-[0.25rem] rounded-[0.5rem] bg-gms-warn-soft px-[0.75rem] py-[0.5rem] text-[0.8125rem] text-gms-warn">
					<b className="font-semibold">{t("hotel.sheet.skipped", { count: result.skipped.length, n: formatNumber(result.skipped.length) })}</b>
					<ul className="grid gap-[0.125rem]">
						{result.skipped.map((r) => (
							<li key={r.room_assignment_id}>{t("hotel.sheet.skipLine", { name: nameOf(r.room_assignment_id), reason: t(`refusal.${r.reason}`) })}</li>
						))}
					</ul>
				</div>
			)}
			{people.isPending ? (
				<div className="grid gap-[0.5rem]" aria-busy>
					<div className="skeleton h-[3.5rem] rounded-[0.5rem]" />
					<div className="skeleton h-[3.5rem] rounded-[0.5rem]" />
				</div>
			) : people.isError ? (
				<p role="alert" className="text-[0.8125rem] text-gms-bad">
					{t("hotel.sheet.loadFailed")}
				</p>
			) : (
				<ul className="grid">
					{guests.map((g) => {
						const can = canCheck && actionable.some((a) => a.room_assignment_id === g.room_assignment_id);
						return (
							<li key={g.room_assignment_id} className="border-t border-gms-line py-[0.625rem] first:border-t-0 first:pt-0">
								<GuestRow
									guest={g}
									lead={
										canCheck ? (
											<input
												type="checkbox"
												className="checkbox checkbox-sm checkbox-primary"
												aria-label={t("hotel.sheet.pick", { name: g.name })}
												checked={can && selected.has(g.room_assignment_id)}
												disabled={!can || busy}
												onChange={() => toggle(g.room_assignment_id)}
											/>
										) : undefined
									}
									end={canCheck ? <UndoCheck guest={g} timeZone={timeZone} /> : undefined}
								/>
							</li>
						);
					})}
				</ul>
			)}
		</Modal>
	);
}
